import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Grid,
  Button,
  Menu,
  MenuItem,
  Alert,
  Card,
  CardContent,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import MoreVert from '@mui/icons-material/MoreVert';
import LocalShipping from '@mui/icons-material/LocalShipping';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Cancel from '@mui/icons-material/Cancel';
import AccessTime from '@mui/icons-material/AccessTime';
import RefreshIcon from '@mui/icons-material/Refresh';
import ShippingIcon from '@mui/icons-material/LocalShipping';
import LogisticsIcon from '@mui/icons-material/EmojiTransportation';
import { useAuth } from '../../context/AuthContext';
import { useShop } from '../../context/ShopContext';
import { Order } from '../../data/mockOrders';
import { useNavigate } from 'react-router-dom';
import { DataService } from '../../services/dataService';
import { NotificationService } from '../../services/errorService';

const SellerOrders: React.FC = () => {
  const { user } = useAuth();
  const { activeShop, shops, setActiveShopId } = useShop();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [openLogistics, setOpenLogistics] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);

  const fetchShopOrders = async () => {
    if (!activeShop) return;
    setLoading(true);
    try {
      const data = await DataService.getShopOrders(activeShop.id);
      // Map database order_items to the Order interface structure
      const mappedOrders = data.map((item: any) => ({
        id: item.order.id,
        date: item.order.created_at,
        customer: 'Customer', // Would need a join with profiles
        total: item.total_price,
        status: item.order.status,
        items: [
          {
            name: item.product_name,
            quantity: item.quantity,
            price: item.price
          }
        ],
        shippingAddress: item.order.shipping_address
      }));
      setOrders(mappedOrders);
    } catch (error: any) {
      NotificationService.error('Error', 'Failed to fetch shop orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShopOrders();
  }, [activeShop]);

  // Check if user has at least one shop OR has a legacy seller role
  const canManageOrders = ['farmer', 'seller', 'admin'].includes(user?.role || '') || (shops && shops.length > 0);

  if (!canManageOrders) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Access Denied: You need to be a registered Seller or Farmer to manage orders.
          Please create a shop first if you haven't already.
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => navigate('/dashboard/my-shops')} 
          sx={{ mt: 2 }}
        >
          Go to My Shops
        </Button>
      </Box>
    );
  }

  // Shop selection prompt if no active shop
  if (!activeShop) {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <Typography variant="h5" gutterBottom>Select a Shop to Manage Orders</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          You need to select a shop before you can manage its orders.
        </Typography>
        
        {shops.length === 0 ? (
          <Button variant="contained" onClick={() => navigate('/dashboard/my-shops')}>
            Create Your First Shop
          </Button>
        ) : (
          <Grid container spacing={2} sx={{ maxWidth: 600 }}>
            {shops.map(shop => (
              <Grid item xs={12} sm={6} key={shop.id}>
                <Card 
                  sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 } }} 
                  onClick={() => setActiveShopId(shop.id)}
                >
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h6">{shop.name}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered': return 'success';
      case 'shipped': return 'info';
      case 'processing': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const handleUpdateStatus = (orderId: string, newStatus: string) => {
    setOrders((prev) =>
      prev.map((order) =>
        order.id === orderId ? { ...order, status: newStatus } : order
      )
    );
  };

  const handleBookLogistics = (order: any) => {
    setSelectedOrder(order);
    setOpenLogistics(true);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h5" fontWeight="bold">
          Orders Received
        </Typography>
        <Stack direction="row" spacing={2}>
          <IconButton onClick={fetchShopOrders} disabled={loading}>
            <RefreshIcon />
          </IconButton>
          <Button
            variant="outlined"
            onClick={() => setActiveShopId(null)}
          >
            Switch Shop
          </Button>
        </Stack>
      </Box>

      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
        Managing orders for: <strong>{activeShop.name}</strong>
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Order ID</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 10 }}>
                  <Typography variant="body1" color="text.secondary">No orders found for this shop.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              orders.map((order) => (
                <Row 
                  key={order.id} 
                  order={order} 
                  onUpdateStatus={handleUpdateStatus} 
                  onBookLogistics={handleBookLogistics}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Logistics Booking Dialog */}
      <Dialog open={openLogistics} onClose={() => setOpenLogistics(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <LogisticsIcon color="primary" /> Book Smart Logistics
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Recommended Partner</Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'primary.50', borderColor: 'primary.light' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body1" fontWeight="bold">HUB Express Transport</Typography>
                  <Typography variant="caption" color="text.secondary">Verified Partner • 4.9/5 Rating</Typography>
                </Box>
                <Typography variant="h6" color="primary.main">KSh 1,200</Typography>
              </Stack>
            </Paper>

            <Typography variant="subtitle2" gutterBottom>Delivery Destination</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {selectedOrder?.shippingAddress?.address || selectedOrder?.shippingAddress || 'Nairobi, Kenya'}
            </Typography>

            <Alert severity="info" sx={{ mb: 2 }}>
              Funds will be released from escrow once delivery is confirmed by the buyer.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenLogistics(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => {
            handleUpdateStatus(selectedOrder.id, 'shipped');
            setOpenLogistics(false);
            NotificationService.success('Success', 'Logistics booked and order marked as shipped!');
          }}>
            Confirm Booking
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

interface RowProps {
  order: any;
  onUpdateStatus: (id: string, status: string) => void;
  onBookLogistics: (order: any) => void;
}

const Row: React.FC<RowProps> = ({ order, onUpdateStatus, onBookLogistics }) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleStatusChange = (status: string) => {
    onUpdateStatus(order.id, status);
    handleMenuClose();
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'delivered': return 'success';
      case 'shipped': return 'info';
      case 'processing': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  return (
    <React.Fragment>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row" sx={{ fontWeight: 'medium' }}>
          #{order.id.slice(0, 8)}...
        </TableCell>
        <TableCell>{new Date(order.date).toLocaleDateString()}</TableCell>
        <TableCell>{order.customer}</TableCell>
        <TableCell sx={{ fontWeight: 'bold' }}>KSh {order.total.toLocaleString()}</TableCell>
        <TableCell>
          <Chip 
            label={order.status.toUpperCase()} 
            size="small" 
            color={getStatusColor(order.status) as any}
            variant="outlined"
            sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}
          />
        </TableCell>
        <TableCell align="right">
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              size="small"
              variant="outlined"
              startIcon={<LogisticsIcon />}
              onClick={() => onBookLogistics(order)}
              disabled={order.status === 'delivered' || order.status === 'cancelled'}
              sx={{ borderRadius: '8px' }}
            >
              Logistics
            </Button>
            <IconButton size="small" onClick={handleMenuClick}>
              <MoreVert />
            </IconButton>
          </Stack>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
            <MenuItem onClick={() => handleStatusChange('processing')}>Mark as Processing</MenuItem>
            <MenuItem onClick={() => handleStatusChange('shipped')}>Mark as Shipped</MenuItem>
            <MenuItem onClick={() => handleStatusChange('delivered')}>Mark as Delivered</MenuItem>
            <MenuItem onClick={() => handleStatusChange('cancelled')} sx={{ color: 'error.main' }}>Cancel Order</MenuItem>
          </Menu>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Typography variant="h6" gutterBottom component="div" sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
                Order Details
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Items</Typography>
                  <List dense>
                    {order.items.map((item: any, index: number) => (
                      <ListItem key={index} disableGutters>
                        <ListItemText 
                          primary={item.name} 
                          secondary={`${item.quantity} x KSh ${item.price.toLocaleString()}`} 
                        />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Shipping Address</Typography>
                  <Typography variant="body2">
                    {typeof order.shippingAddress === 'string' ? order.shippingAddress : (
                      <>
                        {order.shippingAddress?.address || 'N/A'}<br />
                        {order.shippingAddress?.city || ''} {order.shippingAddress?.postalCode || ''}
                      </>
                    )}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

export default SellerOrders;
